import { useMemo, type ReactNode } from 'react';
import createCache from '@emotion/cache';
import { CacheProvider } from '@emotion/react';
import CssBaseline from '@mui/material/CssBaseline';
import { StyledEngineProvider, ThemeProvider } from '@mui/material/styles';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import { createFmsTheme } from './create-theme';

/**
 * Shared theme shell for both portals. `enableCssLayer` emits MUI/Emotion
 * styles into the `mui` CSS layer so Tailwind utilities (higher layer) always
 * win without specificity hacks. RTL wiring is created lazily per direction.
 */
export function AppThemeProvider({
  children,
  direction = 'ltr',
}: {
  children: ReactNode;
  direction?: 'ltr' | 'rtl';
}) {
  const cache = useMemo(
    () =>
      createCache({
        key: direction === 'rtl' ? 'fms-rtl' : 'fms',
        stylisPlugins: direction === 'rtl' ? [prefixer, rtlPlugin] : [prefixer],
        // Requires <meta name="emotion-insertion-point"> — keeps MUI below utilities.
        insertionPoint:
          typeof document !== 'undefined'
            ? (document.querySelector('meta[name="emotion-insertion-point"]') as
                HTMLElement | undefined)
            : undefined,
      }),
    [direction],
  );
  const theme = useMemo(() => createFmsTheme(direction), [direction]);

  return (
    <CacheProvider value={cache}>
      <StyledEngineProvider enableCssLayer>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          {children}
        </ThemeProvider>
      </StyledEngineProvider>
    </CacheProvider>
  );
}
